import { Fragment } from 'react';
import { Icon } from '@iconify/react';
import roundCheckCircle from '@iconify/icons-ic/round-check-circle';
import roundWarning from '@iconify/icons-ic/round-warning';
import { Grid, Paper, Typography, Stack, Chip, Tooltip, Avatar } from '@material-ui/core';
import { capitalize, startCase } from 'lodash';
import { useMediaQuery, useTheme } from '@material-ui/core';
import moment from 'moment';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'src/atoms/user';
import HistoryDrawer from 'src/components/DoctorRequestPage/History/HistoryDrawer';
import Image from 'src/components/Image';

const stateColors = {
  PENDING_DR_REVIEW: 'lightgray',
  PENDING_ADMIN_REVIEW: 'lightblue',
  PENDING_FINANCE_REVIEW: 'lightyellow',
  APPROVED: 'lightgreen',
  DISAPPROVED: '#ffcccb'
};
const RequestHeader = ({
  data,
  usd = 0,
  showBalance = false,
  showBills = true,
  showHistory = false,
  // Employee view only: the backend sends null until admin approval.
  approvedAmountEgp = null
}) => {
  const showApproved = approvedAmountEgp !== null && approvedAmountEgp !== undefined;
  const billWidth = showApproved ? 3 : 4;
  const user = useRecoilValue(userAtom);
  const employeeName = data.request.beneficiary
    ? data.request.beneficiary.name
    : data.request.employee.name;
  const relationship = data?.request?.beneficiary?.relationship;
  // data? of the request owner
  const gender = data?.request?.beneficiary?.gender || data?.request?.employee?.gender;
  const age = moment().diff(
    data.request.beneficiary ? data.request.beneficiary.birthdate : data.request.employee.birthdate,
    'years',
    false
  );

  const office = data?.request?.employee?.office || data?.request?.beneficiary?.employee?.office;
  const request_pic = data.request.beneficiary
    ? data.request.beneficiary.profile_picture
    : data.request.employee.profile_picture;
  const matches = useMediaQuery('(max-width:950px)');
  const theme = useTheme();

  return (
    <Fragment>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <div>
          <Grid
            item
            container
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {' '}
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              {data.request && (
                <Avatar
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%'
                  }}
                >
                  {request_pic && (
                    <Image
                      width="60px"
                      height="60px"
                      imgKey={request_pic}
                      alt={employeeName || 'Profile Pic'}
                    />
                  )}
                </Avatar>
              )}
              <Typography
                variant="h3"
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: '5px'
                }}
              >
                {employeeName}
              </Typography>
            </Stack>
          </Grid>

          <Grid
            item
            container
            style={{
              marginTop: '20px'
            }}
          >
            <Typography variant="subtitle1">
              {capitalize(gender || '')}, {age} Years Old
            </Typography>
            <Typography>
              <Typography
                variant="subtitle1"
                style={{
                  position: 'relative'
                }}
              ></Typography>
            </Typography>
          </Grid>
          <Grid item container>
            <Typography variant="subtitle1" color={'textSecondary'}>
              {startCase(data.request.beneficiary ? data.request.employee.name : '')}
              {relationship && `'s ${capitalize(relationship || '')}, `} Employee ID: (
              {data.request.employee.employeeId}), {capitalize(office || '')} Office
            </Typography>
          </Grid>
        </div>
        {['DOCTOR', 'ADMIN'].includes(user.role) && showHistory ? (
          <HistoryDrawer
            id={data?.request?.beneficiary?.id || data?.request?.employee?.id}
            reqId={data?.request?.id}
          />
        ) : null}
      </Stack>
      <Grid
        item
        container
        style={{
          marginTop: '20px',
          marginBottom: matches ? '10px' : '0px'
        }}
      >
        <Chip
          label={
            <Stack justifyContent={'center'} alignItems={'center'} gap={1} direction={'row'}>
              <CalendarMonthRoundedIcon fontSize={'small'} />
              <Typography variant="">
                {moment(data.request.created_at_date).format('DD MMM YYYY').toString()}
              </Typography>
            </Stack>
          }
          style={{
            backgroundColor: theme.palette.grey[300],
            marginRight: '5px'
          }}
        />
        <Chip
          label={data.request.status.replace(/_/g, ' ')}
          style={{
            backgroundColor: stateColors[data.request.status],
            marginRight: '5px'
          }}
        />
        <Chip
          style={{ marginRight: '5px' }}
          label={data.request.specialization.specialization_description}
          variant="outlined"
        />
        {user.role === 'ADMIN' && showBalance ? (
          <Chip
            label={
              data.request.status === 'PENDING_ADMIN_REVIEW' ||
              data.request.status === 'PENDING_DR_REVIEW' ||
              data.request.status === 'PENDING_FINANCE_REVIEW' ||
              data.request.status === 'OPEN' ? (
                <Tooltip
                  placement="top"
                  arrow
                  title={
                    data.request.employee.balance >= usd
                      ? `Balance After Deduction: $${Number(
                          data.request.employee.balance - usd
                        ).toFixed(2)}`
                      : `Balance After Deduction: ${Number(
                          Math.abs(data.request.employee.balance - usd)
                        ).toFixed(2)} USD (Insufficient Balance)`
                  }
                >
                  <Stack justifyContent={'center'} alignItems={'center'} direction={'row'} gap={1}>
                    <Icon
                      icon={data.request.employee.balance >= usd ? roundCheckCircle : roundWarning}
                      fontSize={theme.spacing(2)}
                    />
                    {data.request.employee.balance >= usd
                      ? 'Sufficient Balance: '
                      : 'Insufficient Balance: '}
                    ${Number(data.request.employee.balance).toFixed(2)}
                  </Stack>
                </Tooltip>
              ) : (
                `Updated Balance: $${Number(data.request.employee.balance).toFixed(2)}`
              )
            }
            variant="contained"
            style={{
              backgroundColor:
                data.request.employee.balance >= usd
                  ? theme.palette.success.lighter
                  : theme.palette.error.lighter,
              border: `1px solid ${
                data.request.employee.balance >= usd
                  ? theme.palette.success.dark
                  : theme.palette.error.dark
              }`,
              color:
                data.request.employee.balance >= usd
                  ? theme.palette.success.dark
                  : theme.palette.error.dark,
              marginRight: '5px'
            }}
          />
        ) : null}
        {user.role === 'ADMIN' && !data.request.doctor_approved ? (
          <Chip
            label={
              <Stack justifyContent={'center'} alignItems={'center'} direction={'row'} gap={1}>
                <Icon icon={roundWarning} fontSize={theme.spacing(2)} />
                Not approved by Doctor
              </Stack>
            }
            variant="outlined"
            style={{
              backgroundColor: theme.palette.error.lighter,
              border: `1px solid ${theme.palette.error.dark}`,
              color: theme.palette.error.dark,
              marginRight: '5px'
            }}
          />
        ) : null}
        {showBills && (
          <Grid
            item
            container
            spacing={3}
            md={12}
            xs={12}
            marginBottom={6}
            style={{
              marginTop: '20px',
              marginBottom: matches ? '10px' : '0px',
              width: '100%'
            }}
          >
            <Grid item container md={billWidth} xs={12}>
              <Paper elevation={3} style={{ padding: '20px', width: '100%' }}>
                <Typography style={{ fontWeight: 'bold' }}>Doctor or Hospital Bill:</Typography>
                {data?.request?.dr_receipt_bill} EGP
              </Paper>
            </Grid>
            <Grid item container md={billWidth} xs={12}>
              <Paper elevation={3} style={{ padding: '20px', width: '100%' }}>
                <Typography style={{ fontWeight: 'bold' }}>Medicine Bill:</Typography>
                {data?.request?.medications_bill} EGP
              </Paper>
            </Grid>
            <Grid item container md={billWidth} xs={12}>
              <Paper elevation={3} style={{ padding: '20px', width: '100%' }}>
                <Typography style={{ fontWeight: 'bold' }}>Rays or Analysis Bill:</Typography>
                {data?.request?.raysOrAnalysisBill} EGP
              </Paper>
            </Grid>
            {showApproved && (
              <Grid item container md={billWidth} xs={12}>
                <Paper elevation={3} style={{ padding: '20px', width: '100%' }}>
                  <Typography style={{ fontWeight: 'bold', color: '#0F52BA' }}>
                    Approved amount:
                  </Typography>
                  {Number(approvedAmountEgp).toFixed(2)} EGP
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </Fragment>
  );
};

export default RequestHeader;
